import React, { useEffect, useCallback, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { ServiceProviderR } from "../routes";
import {
  ProcManagementListService,
  ContextProps,
} from "../../../../application/service/service-provider/proc-management/list";
import { connectPageService } from "../../../../application/domain/base/base-application-service";
import {
  MainMenu,
  NavigationMenu,
} from "../../../molecules/proc-management/list";
import { ProcsTable } from "../../../organisms/proc-management/list";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { StatusValue } from "../../../../application/domain/proc-management";
import { useAuthContextV2 } from "../../../../auth-state";
import { defaultFetchStatuses } from "../../../../application/service/proc-management/proc-list-base";
import { ensureStatusValueArr } from "../../../../application/domain/proc-management/util/status-query-converter";
import { FeatureEnabledOrgOnly } from "../../../organisms/proc-management/util";

const OrganizationProcManagement: React.FC<ContextProps> = ({
  procedures,
  paging,
  fetchProcsByOrgId,
  setAuthCtx,
}) => {
  const { orgId } = useParams<{ orgId: string }>();
  const location = useLocation();
  const { page, q, status } = parseQueryOfLocation(location);
  const pageNum =
    Number.isInteger(Number(page)) && Number(page) > 0
      ? Number(page)
      : undefined;
  const queryStr = typeof q === "string" && q.length > 0 ? q : undefined;
  const selectedStatuses = useMemo(
    () =>
      typeof status === "string" && status.length > 0
        ? ensureStatusValueArr(status.split(","))
        : [],
    [status],
  );

  const { auth } = useAuthContextV2();

  useEffect(() => {
    setAuthCtx(auth.data);
  }, [setAuthCtx, auth]);

  useEffect(() => {
    if (selectedStatuses.length > 0) {
      fetchProcsByOrgId({
        organizationId: orgId,
        page: pageNum,
        query: queryStr,
        statuses: selectedStatuses,
      });
    }
  }, [fetchProcsByOrgId, orgId, pageNum, queryStr, selectedStatuses]);

  const history = useHistory();
  const handleRowClick = useCallback(
    ({ orgId, procId }: { orgId: string; procId: string }) => {
      history.push(
        ServiceProviderR.ProcManagementDetail.link({
          orgId,
          procId,
        }),
      );
    },
    [history],
  );
  const handlePageClick = useCallback(
    ({ orgId, page }: { orgId: string; page: number }) => {
      history.push(
        ServiceProviderR.ProcManagementProcsList.link({
          orgId,
          page,
          q: queryStr,
          statuses: selectedStatuses,
        }),
      );
    },
    [history, queryStr, selectedStatuses],
  );

  const handleSearch = useCallback(
    (query: string) => {
      history.push(
        ServiceProviderR.ProcManagementProcsList.link({
          orgId,
          q: query,
          statuses: selectedStatuses,
        }),
      );
    },
    [history, orgId, selectedStatuses],
  );

  const handleStatusFilter = useCallback(
    (statuses: StatusValue[]) => {
      history.push(
        ServiceProviderR.ProcManagementProcsList.link({
          orgId,
          q: queryStr,
          statuses,
        }),
      );
    },
    [history, orgId, queryStr],
  );

  const handleClear = useCallback(() => {
    history.push(
      ServiceProviderR.ProcManagementProcsList.link({
        orgId,
        statuses: selectedStatuses,
      }),
    );
  }, [history, orgId, selectedStatuses]);

  const breadcrumbs = [
    { to: ServiceProviderR.Home.link({}), title: "テナント一覧" },
    {
      to: ServiceProviderR.ProcManagementProcsList.link({ orgId }),
      title: "手続きマスタ",
    },
  ];

  return (
    <FeatureEnabledOrgOnly>
      <ServiceProviderTemplate breadcrumbs={breadcrumbs}>
        <NavigationMenu orgId={orgId} />
        <MainMenu
          orgId={orgId}
          query={queryStr}
          onSearch={handleSearch}
          onClear={handleClear}
        />
        <ProcsTable
          procedures={procedures}
          orgId={orgId}
          paging={paging}
          selectedStatuses={selectedStatuses}
          defaultStatuses={[...defaultFetchStatuses]}
          query={queryStr}
          onSelectStatus={handleStatusFilter}
          onRowClick={handleRowClick}
          onPageClick={handlePageClick}
        />
      </ServiceProviderTemplate>
    </FeatureEnabledOrgOnly>
  );
};

export const ProcManagementListContainer = connectPageService(
  OrganizationProcManagement,
  () => new ProcManagementListService(),
);
